import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiArrowLeft, FiBookOpen } from 'react-icons/fi';
import api from '../config/api';
import { usePermissions } from '../context/PermissionsContext';
import { usePageTitle } from '../context/PageChromeContext';
import IconButton from '../components/IconButton';
import JournalEditor from '../components/erp/JournalEditor';
import { queryKeys } from '../config/queryKeys';
import { appAlert } from '../utils/appDialog';
import './Services.css';
import '../components/erp/Erp.css';

/** Full-page edit screen for a draft journal entry. */
const JournalEntryEditDetails = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPermission } = usePermissions();

  const canManage = hasPermission('erp_journals_manage');
  const canPost = hasPermission('erp_journals_post');
  const { journalId } = useParams();
  const [searchParams] = useSearchParams();
  const fallbackCompanyId = searchParams.get('company_id') || '';

  usePageTitle('Edit journal entry');

  const { data: detail, isLoading, isError, error } = useQuery(
    ['erp-journal', journalId],
    () => api.get(`/accounting/journals/${journalId}`).then((r) => r.data),
    { enabled: Boolean(journalId) && canManage, staleTime: 0 }
  );

  const resolvedCompanyId = detail?.company_id || fallbackCompanyId || '';

  const accounts = useQuery(
    [...queryKeys.accountingAccounts, resolvedCompanyId || 'none'],
    async () =>
      (
        await api.get('/accounting/accounts', {
          params: resolvedCompanyId ? { company_id: resolvedCompanyId } : undefined,
        })
      ).data,
    { enabled: canManage && Boolean(resolvedCompanyId) }
  );

  const back = () => navigate('/erp/accounting?tab=Journals', { replace: true });

  const saveDraft = useMutation(
    async (payload) => (await api.put(`/accounting/journals/${journalId}`, payload)).data,
    {
      onSuccess: () => {
        qc.invalidateQueries('erp-journals');
        qc.invalidateQueries(queryKeys.accountingJournals);
        back();
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const saveAndPost = useMutation(
    async (payload) => {
      await api.put(`/accounting/journals/${journalId}`, payload);
      if (canPost) {
        await api.post(`/accounting/journals/${journalId}/post`);
      }
      return true;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('erp-journals');
        qc.invalidateQueries(queryKeys.accountingJournals);
        back();
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  if (!canManage) {
    return (
      <div className="svc-package-page list-page erp-page">
        <div className="svc-package-toolbar page-toolbar">
          <IconButton icon={FiArrowLeft} label="Back" onClick={back} />
        </div>
        <div className="svc-package-page-card">
          <div className="error">You do not have permission to edit journal entries.</div>
        </div>
      </div>
    );
  }

  if (isLoading) return <div className="loading">Loading journal…</div>;

  if (isError || !detail) {
    return (
      <div className="svc-package-page list-page erp-page">
        <div className="svc-package-toolbar page-toolbar">
          <IconButton icon={FiArrowLeft} label="Back" onClick={back} />
        </div>
        <div className="svc-package-page-card">
          <div className="error">{error?.response?.data?.error || error?.message || 'Not found'}</div>
        </div>
      </div>
    );
  }

  if (detail.status !== 'draft') {
    return (
      <div className="svc-package-page list-page erp-page">
        <div className="svc-package-toolbar page-toolbar">
          <IconButton icon={FiArrowLeft} label="Back" onClick={back} />
        </div>
        <div className="svc-package-page-card">
          <div className="error">Only draft journals can be edited.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="svc-package-page list-page erp-page">
      <div className="svc-package-toolbar page-toolbar">
        <IconButton icon={FiArrowLeft} label="Back to journals" onClick={back} />
      </div>
      <div className="svc-package-page-card">
        <div className="svc-package-page-header">
          <h1>
            <FiBookOpen aria-hidden /> Edit draft {detail.entry_number}
          </h1>
          <p className="page-subtitle">Update lines, save draft, then Post when balanced.</p>
        </div>
        <JournalEditor
          accounts={accounts.data || []}
          initial={detail}
          saving={saveDraft.isLoading || saveAndPost.isLoading}
          onSave={(payload) => saveDraft.mutate(payload)}
          onPost={canPost ? (payload) => saveAndPost.mutate(payload) : undefined}
        />
      </div>
    </div>
  );
};

export default JournalEntryEditDetails;

